<template>
  <page-wrapper sticky-second-row error-key="legals" :transparent-bg="isGridView">
    <template #title>
      <div class="d-flex justify-space-between align-center w-100">
        <div class="h3">Юридические лица</div>

        <view-mode-toggle v-if="$vuetify.display.mdAndUp" v-model="viewMode" />
      </div>
    </template>

    <template #secondRow>
      <div class="d-flex align-center">
        <legals-filters v-model="filters" />
        <v-spacer />

        <e-btn
          v-if="isOperationVisible(legals?.operations?.canAdd)"
          :to="{ name: RouteNames.LegalCreate }"
          class="add-legal-button"
          prepend-icon-src="plus"
          :icon="$vuetify.display.smAndDown"
          :disabled="isOperationDisabled(legals?.operations?.canAdd)"
          variant="primary"
        >
          <template v-if="!$vuetify.display.smAndDown"> Создать </template>
        </e-btn>

        <e-icon
          v-if="legals?.operations?.onApprovalLimitExceeded && !isLimitAlertVisible"
          name="alert"
          size="20"
          class="text-blue cursor-pointer ml-4"
          @click="setIsLimitAlertVisible(true)"
        />
      </div>
    </template>

    <e-alert v-model="isLimitAlertVisible" variant="info" closable class="bg-white mb-4">
      <div class="paragraph-m_h font-600">Вы не можете добавить новое юридическое лицо</div>
      <div class="paragraph-s_h text-secondary mt-1">
        У вас два юридических лица на&nbsp;рассмотрении. Вы сможете добавить новое юридическое лицо, когда одно
        из&nbsp;них пройдёт проверку
      </div>
    </e-alert>

    <e-data-items
      v-if="viewMode === ViewMode.Table && $vuetify.display.mdAndUp"
      class="mt-2"
      :headers="tableHeaders"
      :items="filteredLegals"
      :loading="isLoading"
      interactive
      sticky-header-top="76px"
      @on-row-click="handleRowClick"
    >
      <template v-slot:[`item.approveStatus`]="{ item }">
        <status-string
          :text="LEGAL_APPROVE_STATUS_MAP[item.approveStatus].name"
          :variant="LEGAL_APPROVE_STATUS_MAP[item.approveStatus].color"
        />
      </template>
    </e-data-items>

    <div v-else class="legals-grid">
      <template v-if="isLoading">
        <skeleton-loader v-for="item in 10" height="200px" :key="item" />
      </template>
      <template v-else>
        <legal-card v-for="item in filteredLegals" :key="item.cid" :legal="item" />
      </template>
    </div>
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import type { LegalsListItemType } from '@/parts/legals/store/legals';
import StatusString from '@/core/components/common/StatusString.vue';
import ViewModeToggle from '@/core/components/common/ViewModeToggle.vue';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EAlert, EBtn, EIcon } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import useToggle from '@/core/composables/useToggle';
import { useUserPreference } from '@/core/composables/useUserPreferences';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { ViewMode } from '@/core/types/components/helps';
import { isOperationDisabled, isOperationVisible } from '@/core/utils/permissions';
import { containsSubstring } from '@/core/utils/string';
import LegalCard from '@/parts/legals/components/LegalCard.vue';
import LegalsFilters from '@/parts/legals/components/LegalsFilters.vue';
import { useLegalsStore } from '@/parts/legals/store/legals';
import { LEGAL_APPROVE_STATUS_MAP } from '@/parts/legals/utils/consts';

const display = useDisplay();
const $router = useRouter();
const { legals, getLegals } = useLegalsStore();
const [isLimitAlertVisible, setIsLimitAlertVisible] = useToggle();

const viewMode = useUserPreference('legalPageViewMode', ViewMode.Table);

const filters = reactive({
  searchString: '',
});

const isGridView = computed(() => viewMode.value === ViewMode.Grid || display.smAndDown.value);

const tableHeaders = [
  {
    key: 'name[0].value',
    title: 'Юридическое лицо',
  },
  {
    key: 'legalDetails.tin',
    title: 'ИНН',
    width: 160,
  },
  {
    key: 'legalDetails.kpp',
    title: 'КПП',
    width: 160,
  },
  {
    key: 'approveStatus',
    title: 'Статус',
    width: 200,
  },
];

const filteredLegals = computed(
  () =>
    legals.value?.legals.data?.filter(
      (item) =>
        !filters.searchString ||
        containsSubstring(item.name[0].value, filters.searchString) ||
        containsSubstring(item.legalDetails.tin, filters.searchString) ||
        (item.legalDetails.kpp && containsSubstring(item.legalDetails.kpp, filters.searchString)),
    ) || [],
);

const handleRowClick = (e: Event, row: { item: LegalsListItemType }) => {
  void $router.push({ name: RouteNames.LegalDetail, params: { id: row.item.cid } });
};

const { isLoading } = useFetch(async () => {
  await getLegals();

  if (legals.value?.operations?.onApprovalLimitExceeded) {
    setIsLimitAlertVisible(true);
  }
});
</script>

<style scoped lang="scss">
.legals-grid {
  display: grid;

  grid-gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @include lg-and-down {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @include sm-and-down {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.add-legal-button {
  @include sm-and-down {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: $z-index-3;
  }
}
</style>
